/**
 * THE CLOSED VOCABULARIES, READ RATHER THAN MIRRORED.
 *
 * `GET /api/vocabularies` sends every value a screen has to render with the
 * word that goes beside it. Nothing in this file knows any of those words: it
 * fetches the list and looks a value up in it. That is the whole point — a
 * second copy of the words in the browser is how ANNUAL_ACCOUNTS ends up
 * printed at an accountant.
 *
 * It is served on its own endpoint because the create-an-engagement control
 * needs the words on an empty screen, before any client or engagement exists.
 */
import { useEffect, useState } from "react";
import { VocabulariesResponse, type Named } from "@leveri/contracts";
import { call, failureMessage } from "../api.js";

export interface Vocabularies {
  /** `null` until the words have arrived. */
  terms: VocabulariesResponse | null;
  failure: string | null;
}

export function useVocabularies(): Vocabularies {
  const [terms, setTerms] = useState<VocabulariesResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  useEffect(() => {
    let abandoned = false;
    call("GET", "/api/vocabularies", VocabulariesResponse)
      .then((response) => { if (!abandoned) setTerms(response); })
      .catch((error: unknown) => { if (!abandoned) setFailure(failureMessage(error)); });
    return () => { abandoned = true; };
  }, []);
  return { terms, failure };
}

/**
 * The word for a value.
 *
 * A value the vocabulary does not carry falls back to the value itself rather
 * than to nothing: the record holds that status, and dropping it would hide
 * something a reader needs more than it would spare them an unfamiliar word.
 * Before the words have arrived the value is what there is to show.
 */
export function word(terms: readonly Named[] | undefined, value: string): string {
  return terms?.find((term) => term.value === value)?.label ?? value;
}
